import axios from "axios";
import { type IAssessmentsDataService } from "../types/services";
import { type AssessmentPayload, type ContentPayload, type AssessmentPeopleDetails } from "../types/state";
import {
  formatFiltersV2api,
  mapToV2Filters,
  processPerformanceParams,
  serializeArrayAndFilterNullable,
} from "../../../../utils/queryUtils";
import { type GetPagedDataByIdRequestV3, type WithPagedDataV2 } from "../../../../interfaces/getPagedDataRequest";
import { type TagsToContent } from "../../../../interfaces";
import { v2FilterMap } from "../../../People/types";
import { escapeTerm } from "../../../../utils/searchUtils";
import { type BasePerformanceRequestFilterParams } from "../../Common/models";

let questionResponsesController = new AbortController();
let questionResponsesPeopleController = new AbortController();
let exportAbortController = new AbortController();

// Added to prevent issues with launch darkly resolving late
// with new endpoint versions
let densityAbortController = new AbortController();
let engagementAbortController = new AbortController();
let questionsAbortController = new AbortController();
let peopleDetailsAbortController = new AbortController();

const assessmentsDataService: IAssessmentsDataService = {
  getPublishedAssessmentsByIdsV2(ids: string[], showPurchased: boolean) {
    const config = {
      params: {
        showPurchased,
        ids,
      },
      paramsSerializer: serializeArrayAndFilterNullable,
    };

    return axios.get("/api/v2/assets/assessments/published", config);
  },

  updateAssessmentAsync(data: AssessmentPayload) {
    return axios.put(`/api/assets/assessments/${data.id}`, data);
  },

  updateContentAsync(data: ContentPayload) {
    return axios.put(`/api/assets/assessments/${data.id}/questions`, data);
  },

  getAssessmentV2Async(id: number) {
    return axios.get(`/api/v2/assets/assessments/${id}`);
  },

  getAssessmentContentAsync(id: number) {
    return axios.get(`/api/assets/assessments/${id}/questions`);
  },

  getAssessmentUsersV2Async<T>(entityId: number, request: WithPagedDataV2<T>) {
    const config = {
      params: request,
      paramsSerializer: serializeArrayAndFilterNullable,
    };
    return axios.get(`/api/v4/content/assessment/${entityId}/users`, config);
  },

  getAssessmentGroupsV2Async(request: GetPagedDataByIdRequestV3) {
    const { entityId, skip, top, term, filter } = request;
    return axios.get(`/api/v4/content/assessment/${entityId}/groups`, {
      params: {
        skip,
        top,
        ...formatFiltersV2api(mapToV2Filters(filter!, v2FilterMap)),
        term: escapeTerm(term),
      },
      paramsSerializer: serializeArrayAndFilterNullable,
    });
  },

  duplicateAssessmentsAsync(assessmentIds: number[]) {
    return axios.post(`/api/assets/assessments/duplicate`, { assessmentIds });
  },

  getTagsFilterOptions(showPurchased: boolean) {
    return axios.get("/api/assets/assessments/tags", { params: { showPurchased } });
  },

  getPublishersFilterOptions() {
    return axios.get("/api/assets/assessments/publishers");
  },

  deleteAssessmentAsync(ids: number[]) {
    return axios.delete("/api/assets/assessments", {
      data: { ids },
    });
  },

  // Performance
  async getQuestionResponses(
    assessmentId: number,
    questionId: string,
    filterParams: BasePerformanceRequestFilterParams,
  ) {
    questionResponsesController.abort();
    questionResponsesController = new AbortController();
    const response = await axios.get("/api/reports/v4/assessments/question/details", {
      params: {
        assessmentId,
        questionId,
        ...filterParams,
      },
      signal: questionResponsesController.signal,
      paramsSerializer: processPerformanceParams,
    });
    return response.data;
  },

  async getAssessmentEngagementLine(entityId: number, filterParams: BasePerformanceRequestFilterParams) {
    densityAbortController.abort();
    densityAbortController = new AbortController();
    const response = await axios.get("/api/reports/v4/assessments/linechart", {
      params: {
        assessmentId: entityId,
        ...filterParams,
      },
      signal: densityAbortController.signal,
      paramsSerializer: processPerformanceParams,
    });
    return response.data;
  },

  async getAssessmentEngagementFunnel(entityId: number, filterParams: BasePerformanceRequestFilterParams) {
    engagementAbortController.abort();
    engagementAbortController = new AbortController();
    const response = await axios.get("/api/reports/v4/assessments/engagement", {
      params: {
        assessmentId: entityId,
        ...filterParams,
      },
      signal: engagementAbortController.signal,
      paramsSerializer: processPerformanceParams,
    });
    return response.data;
  },

  async getAssessmentEngagementQuestions(entityId: number, filterParams: BasePerformanceRequestFilterParams) {
    questionsAbortController.abort();
    questionsAbortController = new AbortController();
    const response = await axios.get("/api/reports/v4/assessments/questions", {
      params: {
        assessmentId: entityId,
        ...filterParams,
      },
      signal: questionsAbortController.signal,
      paramsSerializer: processPerformanceParams,
    });
    return response.data;
  },

  async getAssessmentEngagementQuestionsPeople(
    entityId: number,
    questionId: string,
    answerId: string,
    filterParams: BasePerformanceRequestFilterParams,
  ) {
    questionResponsesPeopleController.abort();
    questionResponsesPeopleController = new AbortController();

    return axios.get("/api/reports/v4/assessments/question/answer/users", {
      params: {
        assessmentId: entityId,
        questionId,
        answerId,
        ...filterParams,
      },
      signal: questionResponsesPeopleController.signal,
      paramsSerializer: processPerformanceParams,
    });
  },

  cancelAssessmentQuestionsPeople() {
    questionResponsesPeopleController.abort();
  },

  addAssessmentTags(data: TagsToContent) {
    return axios.post(`/api/assets/assessments/tags`, data);
  },

  saveAssessmentTags(id: number, payload: TagsToContent) {
    return axios.put(`/api/assets/assessments/${id}/tags`, payload);
  },

  async getAssessmentPeopleDetails(
    assessmentId: number,
    filterParams: BasePerformanceRequestFilterParams,
  ): Promise<AssessmentPeopleDetails[]> {
    peopleDetailsAbortController.abort();
    peopleDetailsAbortController = new AbortController();

    const response = await axios.get<{ AssessmentSummary: AssessmentPeopleDetails[] }>(
      "/api/reports/v4/assessments/question/user/details",
      {
        params: { assessmentId, ...filterParams },
        signal: peopleDetailsAbortController.signal,
        paramsSerializer: processPerformanceParams,
      },
    );

    return response.data.AssessmentSummary;
  },

  getExport(assessmentId: number, filterParams: BasePerformanceRequestFilterParams) {
    exportAbortController.abort();
    exportAbortController = new AbortController();
    return axios.get("/api/dataexport/v4/assessments/user/progress", {
      responseType: "blob",
      params: {
        assessmentId,
        ...filterParams,
      },
      signal: exportAbortController.signal,
    });
  },

  changeVisibility(ids: number[], visibility: boolean) {
    return axios.post("/api/assets/assessments/visibility", {
      ids,
      visibility,
    });
  },
};

export default assessmentsDataService;
